/**
 * 監査ログの差分（F-22 / CLAUDE.md 絶対ルール8）。**純粋関数だけ。**
 *
 * ## ★行全体のスナップショットから、変わった列だけを抜く
 *
 * `migrations/0017` の `fn_audit` は**列単位の差分ではなく行全体**を積む。
 * そのまま並べると、たとえば `users` は
 * **ログイン失敗で `failed_login_count` が増えるたびに全列が乗る**ので、
 * 「いつ強制パスワード変更を課したか」がログイン試行のノイズに埋もれる
 * （`.loop/rounds/M2c.md` の申し送り）。
 *
 * ★**変わっていない列を出さない**のが、この画面が読めるかどうかの分かれ目。
 */

/**
 * 監査ログ1行の操作。
 *
 * ★**DB の CHECK は7つ許している**（`migrations/0014`：INSERT / UPDATE / DELETE /
 * LOGIN / EXPORT / APPROVE / REOPEN）。いま書かれているのは `fn_audit` が入れる
 * 上の3つだけだが、**型を3つに閉じると、将来 `LOGIN` の行が来たときに黙って
 * UPDATE として扱う**（2026-08-20・security の指摘）。
 * ★だから型は広く取り、**扱いを知らない操作は「差分だけ出す」に落とす。**
 */
export type AuditAction = "INSERT" | "UPDATE" | "DELETE" | (string & {});

export interface FieldChange {
  field: string;
  /** 変更前。INSERT では `null` */
  before: string | null;
  /** 変更後。DELETE では `null` */
  after: string | null;
}

/** jsonb の値を、画面に出せる1行の文字列にする。★`null` と文字列 `"null"` を混ぜない */
function show(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  // 入れ子（jsonb）はそのまま JSON にする。★中身を畳まない（畳むと差分が消える）
  return JSON.stringify(value);
}

/**
 * 変わった列だけを返す。
 *
 * - `INSERT`：新しい行のうち **`null` でない列**だけ（全列出すと初期値の羅列になる）
 * - `DELETE`：消えた行のうち **`null` でない列**だけ
 * - `UPDATE`：**値が変わった列だけ**
 *
 * ★列の順序は**キー名の昇順に固定する。** jsonb のキー順は保証されないので、
 *   固定しないと同じ行が実行のたびに違う並びで出る。
 */
export function changedFields(
  action: AuditAction,
  oldValues: Record<string, unknown> | null,
  newValues: Record<string, unknown> | null,
): FieldChange[] {
  const keys = [
    ...new Set([...Object.keys(oldValues ?? {}), ...Object.keys(newValues ?? {})]),
  ].sort();

  const changes: FieldChange[] = [];
  for (const field of keys) {
    const before = show(oldValues?.[field]);
    const after = show(newValues?.[field]);

    /* ★INSERT / DELETE 以外は「変わった列だけ」。知らない操作もここへ落ちる */
    if (action === "INSERT") {
      if (after !== null) changes.push({ field, before: null, after });
      continue;
    }
    if (action === "DELETE") {
      if (before !== null) changes.push({ field, before, after: null });
      continue;
    }
    // ★UPDATE（と、扱いを知らない操作）：変わっていない列は出さない
    if (before !== after) changes.push({ field, before, after });
  }
  return changes;
}

/**
 * 見せてよい列だけに絞る（絶対ルール1）。
 *
 * ★**`redactPayroll` / `jsonSafe` には任せられない。** あちらは**キー名**を見るが、
 *   ここで返すのは `{ field: "base_hourly_wage", ... }` という形で、
 *   給与の列名が**キーではなく値**として入る。素通りする。
 *
 * @param payrollFields 給与情報とみなす列名（`src/auth/payroll.ts` の一覧を渡す）
 * @param canViewPayroll 見てよい相手か
 */
export function visibleFields(
  changes: readonly FieldChange[],
  payrollFields: readonly string[],
  canViewPayroll: boolean,
): FieldChange[] {
  if (canViewPayroll) return [...changes];
  return changes.filter((c) => !payrollFields.includes(c.field));
}

/**
 * ★伏せられた値かどうか（`app.mask_secrets` が置き換えた印）。
 *
 * `(マスク済み)` は「その値だった」ではなく「見せない」。
 * **`true` / `false` と同じ見た目で出さない**ため、画面が分岐できるようにここで判定する
 * （`must_change_password` は `true` / `false` / `(マスク済み)` の3値が混在する。
 *  2026-08-20 以前の行は伏せられたまま残っている＝遡及修正はしない方針）。
 */
export const MASKED = "(マスク済み)";

export const isMasked = (value: string | null): boolean => value === MASKED;
